import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "services/AxiosInstance";

const DEFAULT_CACHE_TTL = 5 * 60 * 1000;

const zonasSocialesCache = new Map();
const zonasSocialesInFlight = new Map();

const stableSerialize = (value) => {
  if (value === null || value === undefined) {
    return String(value);
  }

  if (typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(",")}]`;
  }

  const sortedKeys = Object.keys(value).sort();
  return `{${sortedKeys
    .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
    .join(",")}}`;
};

const buildCacheKey = (endpoint, params) =>
  `${endpoint}|${stableSerialize(params || {})}`;

/**
 * Endpoint por defecto para obtener el dropdown de zonas sociales.
 * Mantener sincronizado con los endpoints versionados del backend.
 */
export const ZONAS_SOCIALES_ENDPOINT = "/api/v3/zonas-sociales/zonas/dropdown/";

/**
 * Normaliza el payload devuelto por el backend y elimina duplicados.
 * @param {Array|Object} payload - Respuesta HTTP del endpoint de zonas.
 * @returns {Array<{ uuid: string, nombre: string }>} Lista normalizada.
 */
export const normalizeZonaSocialOptions = (payload) => {
  const rawList = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
      ? payload.results
      : [];

  const normalizedMap = new Map();

  rawList.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }

    const uuidCandidate =
      item.uuid ?? item.value ?? item.id ?? item.slug ?? item.key ?? null;
    const nombreCandidate =
      item.nombre ??
      item.label ??
      item.name ??
      item.descripcion ??
      item.title ??
      "";

    if (!uuidCandidate || !nombreCandidate) {
      return;
    }

    const uuid = String(uuidCandidate);
    const nombre = String(nombreCandidate);

    if (!uuid || !nombre) {
      return;
    }

    normalizedMap.set(uuid, {
      ...item,
      uuid,
      nombre,
    });
  });

  return Array.from(normalizedMap.values());
};

/**
 * Hook reutilizable para consumir el dropdown de zonas sociales.
 *
 * @param {Object} options
 * @param {string} [options.endpoint] - Endpoint personalizado.
 * @param {boolean} [options.autoFetch=true] - Realizar la petición automáticamente.
 * @param {Array} [options.initialOptions=[]] - Datos iniciales para hidratar el hook.
 * @param {Object|Function} [options.params] - Parámetros para el request. Puede ser una función.
 * @param {number} [options.cacheTTL=300000] - Tiempo en milisegundos para reutilizar los resultados cacheados.
 * @param {Function} [options.onError] - Callback en caso de error.
 *
 * @returns {{
 *  zones: Array,
 *  isLoading: boolean,
 *  error: Error|null,
 *  reload: Function,
 *  setZones: Function
 * }}
 */
export const useZonaSocialOptions = ({
  endpoint = ZONAS_SOCIALES_ENDPOINT,
  autoFetch = true,
  initialOptions,
  params,
  cacheTTL = DEFAULT_CACHE_TTL,
  onError,
} = {}) => {
  const serializedInitialOptions = useMemo(
    () =>
      typeof initialOptions === "undefined"
        ? null
        : JSON.stringify(initialOptions),
    [initialOptions],
  );

  const normalizedInitial = useMemo(
    () => normalizeZonaSocialOptions(initialOptions),
    [serializedInitialOptions],
  );
  const hasInitialOptions = serializedInitialOptions !== null;

  const [zones, setZones] = useState(normalizedInitial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const paramsRef = useRef(params);
  const onErrorRef = useRef(onError);
  const cacheTTLRef = useRef(cacheTTL);
  const isMountedRef = useRef(true);

  const setZonesSafe = useCallback(
    (nextZones) => {
      if (isMountedRef.current) {
        setZones(nextZones);
      }
    },
    [setZones],
  );

  const setIsLoadingSafe = useCallback(
    (value) => {
      if (isMountedRef.current) {
        setIsLoading(value);
      }
    },
    [setIsLoading],
  );

  const setErrorSafe = useCallback(
    (value) => {
      if (isMountedRef.current) {
        setError(value);
      }
    },
    [setError],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    cacheTTLRef.current = cacheTTL;
  }, [cacheTTL]);

  useEffect(() => {
    if (!hasInitialOptions) {
      return;
    }

    setZonesSafe(normalizedInitial);
    setErrorSafe(null);
    setIsLoadingSafe(false);

    const resolvedParams =
      typeof paramsRef.current === "function"
        ? paramsRef.current()
        : paramsRef.current;
    const cacheKey = buildCacheKey(endpoint, resolvedParams);

    if (Array.isArray(normalizedInitial) && normalizedInitial.length > 0) {
      zonasSocialesCache.set(cacheKey, {
        data: normalizedInitial,
        timestamp: Date.now(),
      });
    }
  }, [
    endpoint,
    hasInitialOptions,
    normalizedInitial,
    setErrorSafe,
    setIsLoadingSafe,
    setZonesSafe,
  ]);

  const fetchZones = useCallback(
    async ({ force = false } = {}) => {
      const resolvedParams =
        typeof paramsRef.current === "function"
          ? paramsRef.current()
          : paramsRef.current;

      const cacheKey = buildCacheKey(endpoint, resolvedParams);
      const ttl = cacheTTLRef.current ?? DEFAULT_CACHE_TTL;
      const now = Date.now();

      if (!force) {
        const cachedEntry = zonasSocialesCache.get(cacheKey);
        if (cachedEntry && now - cachedEntry.timestamp < ttl) {
          setErrorSafe(null);
          setZonesSafe(cachedEntry.data);
          setIsLoadingSafe(false);
          return cachedEntry.data;
        }

        const inFlightRequest = zonasSocialesInFlight.get(cacheKey);
        if (inFlightRequest) {
          setIsLoadingSafe(true);
          try {
            const data = await inFlightRequest;
            setErrorSafe(null);
            setZonesSafe(data);
            setIsLoadingSafe(false);
            return data;
          } catch (fetchError) {
            setErrorSafe(fetchError);
            const handler = onErrorRef.current;
            if (typeof handler === "function") {
              handler(fetchError);
            }
            setZonesSafe([]);
            setIsLoadingSafe(false);
            return [];
          }
        }
      } else {
        zonasSocialesCache.delete(cacheKey);
        zonasSocialesInFlight.delete(cacheKey);
      }

      setIsLoadingSafe(true);
      setErrorSafe(null);

      let requestPromise;

      const executor = async () => {
        try {
          const response = await axiosInstance.get(endpoint, {
            params: resolvedParams,
          });

          const normalized = normalizeZonaSocialOptions(response?.data);

          zonasSocialesCache.set(cacheKey, {
            data: normalized,
            timestamp: Date.now(),
          });

          return normalized;
        } finally {
          if (zonasSocialesInFlight.get(cacheKey) === requestPromise) {
            zonasSocialesInFlight.delete(cacheKey);
          }
        }
      };

      requestPromise = executor();
      zonasSocialesInFlight.set(cacheKey, requestPromise);

      try {
        const normalized = await requestPromise;
        setErrorSafe(null);
        setZonesSafe(normalized);
        return normalized;
      } catch (fetchError) {
        setErrorSafe(fetchError);
        const handler = onErrorRef.current;
        if (typeof handler === "function") {
          handler(fetchError);
        }
        setZonesSafe([]);
        return [];
      } finally {
        setIsLoadingSafe(false);
      }
    },
    [endpoint, setErrorSafe, setIsLoadingSafe, setZonesSafe],
  );

  useEffect(() => {
    if (!autoFetch) {
      return () => undefined;
    }

    fetchZones();

    return () => undefined;
  }, [autoFetch, fetchZones]);

  const reload = useCallback(() => {
    fetchZones({ force: true });
  }, [fetchZones]);

  return {
    zones,
    isLoading,
    error,
    reload,
    setZones: setZonesSafe,
  };
};

/**
 * Test-only helper to clear cached zonas sociales.
 * Avoid invoking this outside of controlled test environments.
 */
export const __resetZonaSocialOptionsCacheForTests = () => {
  zonasSocialesCache.clear();
  zonasSocialesInFlight.clear();
};

export default useZonaSocialOptions;
